import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left form panel */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <Logo size={44} showWordmark />
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2 tracking-tight">Giriş Yap</h1>
          <p className="text-slate-600 mb-8">Sahaya devam etmek için hesabınıza erişin.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@santiye.com"
                className="mt-1.5 h-12 border-2 border-slate-300 focus-visible:ring-sky-500 text-base"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Şifre</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-12 border-2 border-slate-300 focus-visible:ring-sky-500 text-base"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border-2 border-red-300 text-red-800 text-sm rounded-md" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white font-bold text-base rounded-md"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600 text-center">
            Hesabınız yok mu?{" "}
            <Link to="/register" className="text-sky-500 font-bold hover:underline" data-testid="register-link">
              Kayıt olun
            </Link>
          </div>
        </div>
      </div>

      {/* Right image panel */}
      <div
        className="hidden lg:block relative bg-slate-900"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(15,23,42,0.55), rgba(14,165,233,0.35)), url('https://images.unsplash.com/photo-1517089152318-42ec560349c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxpbmZyYXN0cnVjdHVyZSUyMGNvbnN0cnVjdGlvbiUyMGRyb25lfGVufDB8fHx8MTc4MzE1OTM2M3ww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="uppercase tracking-widest text-sky-300 text-xs font-bold mb-2">Altyapı İmalat Takibi</div>
          <div className="font-display text-4xl xl:text-5xl font-black leading-tight mb-3">
            Sahadaki her metreyi <br /> gözünüzün önünde tutun.
          </div>
          <div className="text-slate-200 max-w-lg">
            9 ana imalat kalemi, günlük ilerlemeler, kalan miktarlar ve kıyaslamalı grafikler tek panelde.
          </div>
        </div>
      </div>
    </div>
  );
}
